// src/hooks/useClientes.js
import { useState, useEffect } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, onSnapshot, query, orderBy, where,
  serverTimestamp, getDoc
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useRol } from "./useRoles";
import toast from "react-hot-toast";

const COL     = "clientes";
const COL_HIS = "historial";

export function useClientes() {
  const [clientes, setClientes] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const { rol, perfil, permisos } = useRol();

  useEffect(() => {
    if (!rol) return;
    let q;
    if (permisos.verTodosClientes) {
      q = query(collection(db, COL), orderBy("creadoEn", "desc"));
    } else {
      const email = auth.currentUser?.email || "";
      q = query(
        collection(db, COL),
        where("responsable", "==", perfil?.nombre || email),
        orderBy("creadoEn", "desc")
      );
    }
    const unsub = onSnapshot(q, snap => {
  setClientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  setLoading(false);
}, (error) => {
  console.error("Error clientes:", error);
  setClientes([]);
  setLoading(false);
});

const timeout = setTimeout(() => setLoading(false), 5000);
return () => { unsub(); clearTimeout(timeout); };
  }, [rol, permisos, perfil]);

  const crearCliente = async (datos) => {
    try {
      await addDoc(collection(db, COL), {
        ...datos,
        creadoEn: serverTimestamp(),
        estado: datos.estado || "Nuevo",
      });
      toast.success("✅ Cliente creado correctamente");
    } catch (e) {
      toast.error("Error al crear cliente");
      console.error(e);
    }
  };

  const actualizarCliente = async (id, datos) => {
    try {
      // Obtener estado anterior para detectar cambio
      const snapAnterior = await getDoc(doc(db, COL, id));
      const estadoAnterior = snapAnterior.exists() ? snapAnterior.data().estado : null;
      const estadoNuevo    = datos.estado;

      // Actualizar el cliente
      await updateDoc(doc(db, COL, id), {
        ...datos,
        actualizadoEn: serverTimestamp(),
      });

      // Si el estado cambió a Cerrado o Perdido — guardar en historial
      if (
        estadoNuevo &&
        estadoAnterior !== estadoNuevo &&
        (estadoNuevo === "Cerrado" || estadoNuevo === "Perdido")
      ) {
        const clienteData = snapAnterior.data();
        await addDoc(collection(db, COL_HIS), {
          clienteId:    id,
          nombre:       clienteData.nombre       || "",
          empresa:      clienteData.empresa      || "",
          proyecto:     clienteData.proyecto     || "",
          responsable:  clienteData.responsable  || "",
          valor:        Number(datos.valor || clienteData.valor) || 0,
          estadoAnterior,
          estadoFinal:  estadoNuevo,
          comentario:   datos.comentarioHistorial || "",
          fecha:        serverTimestamp(),
          creadoPor:    auth.currentUser?.email  || "",
        });
      }

      toast.success("✅ Cliente actualizado");
    } catch (e) {
      toast.error("Error al actualizar");
      console.error(e);
    }
  };

  const eliminarCliente = async (id) => {
    if (!permisos.puedeEliminar) {
      toast.error("No tienes permiso para eliminar");
      return;
    }
    try {
      await deleteDoc(doc(db, COL, id));
      toast.success("🗑 Cliente eliminado");
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const kpis = {
    total:        clientes.length,
    cerrados:     clientes.filter(c => c.estado === "Cerrado").length,
    negociacion:  clientes.filter(c => c.estado === "Negociación").length,
    valorTotal:   clientes.filter(c => c.estado !== "Perdido").reduce((s,c) => s + (Number(c.valor)||0), 0),
    valorCerrado: clientes.filter(c => c.estado === "Cerrado").reduce((s,c) => s + (Number(c.valor)||0), 0),
    conversion:   clientes.length > 0
      ? ((clientes.filter(c => c.estado === "Cerrado").length / clientes.length) * 100).toFixed(1)
      : 0,
    vencidos: clientes.filter(c => { if (!c.proximoSeguimiento) return false; const [y,m,d]=String(c.proximoSeguimiento).split("-").map(Number); const hoy=new Date(); hoy.setHours(0,0,0,0); return new Date(y,m-1,d) < hoy; }).length,
  };

  return { clientes, loading, kpis, crearCliente, actualizarCliente, eliminarCliente };
}